import { Mail } from 'lucide-react'
import type { TeamInvitationDto } from '@lib/dto/team.dto'
import type { Uuid } from '@lib/validators/params'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ROLE_LABELS, ROLE_TONES } from './roles'

interface InvitationListProps {
  invitations: TeamInvitationDto[]
  canManage: boolean
  busyId: Uuid | null
  onResend: (id: Uuid) => void
  onRevoke: (id: Uuid) => void
}

/** Pending invitations, with resend and revoke for those allowed to manage. */
export function InvitationList({ invitations, canManage, busyId, onResend, onRevoke }: InvitationListProps) {
  if (invitations.length === 0) {
    return <p className="text-body text-muted-foreground">No pending invitations.</p>
  }
  return (
    <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card shadow-card">
      {invitations.map((invitation) => (
        <li key={invitation.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Mail className="size-3.5" />
            </span>
            <div className="min-w-0">
              <p className="flex items-center gap-2 truncate font-medium">
                {invitation.email}
                <Badge tone={ROLE_TONES[invitation.role]}>{ROLE_LABELS[invitation.role]}</Badge>
              </p>
              <p className="text-caption text-muted-foreground">
                {invitation.invitedBy ? `Invited by ${invitation.invitedBy} · ` : ''}expires{' '}
                {new Date(invitation.expiresAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          {canManage ? (
            <div className="flex gap-1">
              <Button
                variant="secondary"
                size="sm"
                disabled={busyId === invitation.id}
                onClick={() => onResend(invitation.id)}
              >
                Resend
              </Button>
              <Button
                variant="destructive-quiet"
                size="sm"
                disabled={busyId === invitation.id}
                onClick={() => onRevoke(invitation.id)}
              >
                Revoke
              </Button>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  )
}
